/**
 * Switch Class Schedule Staff — pattern-level staff swap for the future scope
 * of a schedule (effective_date onward). Past sessions keep their original
 * staff for history.
 *
 * Smart Match: candidates come from the pattern-replacement endpoint, which
 * applies the same qualification/availability/conflict guards the save-time
 * transaction re-enforces. Hard-ineligible teachers (inactive, missing
 * grade/subject, location mismatch, calendar conflict) are HIDDEN from the
 * ranked list; the user can opt-in to see them with reasons via "Why isn't
 * someone listed?". No external AI call — ranking is a deterministic function
 * of real database signals (coverage + least-disruptive load).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  MenuItem, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { useNotifier } from "../../../../../Core/Notifier";
import {
  useGetClassSchedulePatternReplacementStaffMutation,
  useSwitchClassScheduleStaffMutation,
} from "../../../../../Api/attoDeskApi";
import AppDatePicker from "../../../../../Components/DatePicker/AppDatePicker";
import type { SchedulePattern } from "../ClassSchedulesTab";
import { ScheduleSummary } from "./AddStudentDialog";

interface Props {
  open: boolean;
  pattern: SchedulePattern;
  onClose: () => void;
  onDone: () => void;
}

/** Reasons the backend accepts via `reason_type`. "Other" forces free text. */
const SWITCH_REASONS = [
  "Staff unavailable",
  "Schedule conflict",
  "Location change",
  "Staff request",
  "Management decision",
  "Leave / absence",
  "Other",
];
const REASON_MAX = 500;

const initials = (n: string) =>
  (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";

interface EligibleStaff {
  id: number;
  name: string;
  image?: string | null;
  matched_grade_subjects?: Array<{ grade: string; subjects: string[] }>;
  study_mode?: "online" | "in_person";
  match_reasons?: string[];
  is_best_match?: boolean;
  smart_score?: number;
}
interface IneligibleStaff {
  id: number;
  name: string;
  image?: string | null;
  reasons: string[];
}

const SwitchStaffDialog: React.FC<Props> = ({ open, pattern, onClose, onDone }) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();

  const [staffId, setStaffId] = useState<number | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [reasonSel, setReasonSel] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [showIneligible, setShowIneligible] = useState<boolean>(false);
  const [tried, setTried] = useState<boolean>(false);

  const [getReplacements, { isLoading: loadingSmart }] = useGetClassSchedulePatternReplacementStaffMutation();
  const [switchStaff, { isLoading: saving }] = useSwitchClassScheduleStaffMutation();

  const [eligible, setEligible] = useState<EligibleStaff[]>([]);
  const [ineligible, setIneligible] = useState<IneligibleStaff[]>([]);
  const [futureCount, setFutureCount] = useState<number>(0);

  // Reset per-open; refetch whenever effective_date changes while the dialog is open.
  useEffect(() => {
    if (!open) return;
    setStaffId(null); setReasonSel(""); setCustomReason(""); setNotes("");
    setSearch(""); setShowIneligible(false); setTried(false);
  }, [open, pattern?.schedule_id]);

  useEffect(() => {
    if (!open || !pattern?.schedule_id || !effectiveDate) return;
    let alive = true;
    (async () => {
      try {
        const res = await getReplacements({ scheduleId: pattern.schedule_id, effective_date: effectiveDate }).unwrap();
        if (!alive) return;
        const d: any = (res as any)?.data ?? {};
        setEligible(Array.isArray(d.available_staff) ? d.available_staff : []);
        setIneligible(Array.isArray(d.unavailable_staff) ? d.unavailable_staff : []);
        setFutureCount(Number(d.future_count ?? 0));
      } catch {
        if (!alive) return;
        setEligible([]); setIneligible([]); setFutureCount(0);
      }
    })();
    return () => { alive = false; };
  }, [open, pattern?.schedule_id, effectiveDate, getReplacements]);

  // Clear selection if the current pick disappears when effective date changes.
  useEffect(() => {
    if (staffId && !eligible.some((s) => s.id === staffId)) setStaffId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  const filteredEligible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter((s) => s.name.toLowerCase().includes(q));
  }, [eligible, search]);

  const effectiveReason = reasonSel === "Other" ? customReason.trim() : reasonSel;
  const reasonValid = reasonSel !== "" && (reasonSel !== "Other" || customReason.trim().length > 0);

  // Date bounds — can't be before the series starts; can't be after it ends.
  const dateMin = pattern?.series_start_date || undefined;
  const dateMax = pattern?.series_end_date || undefined;
  const dateInRange = (() => {
    if (!effectiveDate) return false;
    if (dateMin && effectiveDate < dateMin) return false;
    if (dateMax && effectiveDate > dateMax) return false;
    return true;
  })();

  const canSubmit = !!staffId && reasonValid && dateInRange && !saving && !loadingSmart;

  const submitDisabledReason = !staffId
    ? "Select a replacement teacher."
    : !dateInRange
      ? "Effective date is outside the series range."
      : !reasonValid
        ? "Reason is required."
        : "";

  const handleSubmit = async () => {
    setTried(true);
    if (!canSubmit || !staffId) return;
    try {
      const res = await switchStaff({
        scheduleId: pattern.schedule_id,
        staff_id: staffId,
        effective_date: effectiveDate,
        reason: effectiveReason,
        notes: notes.trim() || undefined,
      }).unwrap();
      if ((res as any)?.status !== false) {
        const affected = (res as any)?.data?.future_occurrences_updated ?? 0;
        showMessage(`Staff switched — ${affected} future session${affected === 1 ? "" : "s"} updated.`);
        onDone();
      } else {
        showErrorMessage((res as any)?.message || "Failed to switch staff.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to switch staff.");
    }
  };

  const currentStaffLine = useMemo(() => {
    const bits: string[] = [];
    if (pattern?.location?.name) bits.push(pattern.location.name);
    if (pattern?.class_type) bits.push(pattern.class_type);
    return bits.join(" · ");
  }, [pattern]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <SwapHorizIcon fontSize="small" /> Switch Class Schedule Staff
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <ScheduleSummary pattern={pattern} />

        {pattern?.staff && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.03em", mb: 0.5 }}>
              Current Staff
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: "10px", border: "1px solid rgba(15,23,42,0.12)" }}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark }}>
                {initials(pattern.staff.name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{pattern.staff.name}</Typography>
                {currentStaffLine && (
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{currentStaffLine}</Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Stack spacing={1.75} sx={{ mt: 2 }}>
          <AppDatePicker
            label="Effective Date *"
            value={effectiveDate}
            onChange={(v) => setEffectiveDate(v)}
            required
            fullWidth
            size="small"
            minDate={dateMin}
            maxDate={dateMax}
            error={!!effectiveDate && !dateInRange}
            helperText={
              !effectiveDate
                ? "Pick the first date the new staff should take over."
                : !dateInRange
                  ? "Effective date is outside this schedule's active range."
                  : "Applies to future occurrences on or after this date. Past sessions keep their original staff."
            }
          />

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                Replacement Staff *
              </Typography>
              <Chip
                size="small"
                icon={<StarRoundedIcon sx={{ fontSize: "14px !important" }} />}
                label="Smart Match"
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, "& .MuiChip-icon": { color: theme.palette.primary.main } }}
              />
            </Box>

            <TextField
              size="small" fullWidth
              placeholder="Search eligible staff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />

            {loadingSmart ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  Finding qualified teachers available for every future session…
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                {filteredEligible.length === 0 && (
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary", py: 1 }}>
                    {search.trim()
                      ? "No eligible staff match that search."
                      : "No teacher is qualified and free for every future occurrence from this date."}
                  </Typography>
                )}
                {filteredEligible.map((s, idx) => {
                  const on = s.id === staffId;
                  const score = typeof s.smart_score === "number" ? `${s.smart_score}%` : null;
                  const gs = (s.matched_grade_subjects || [])
                    .map((g) => `${g.grade} ${(g.subjects || []).join(" & ")}`)
                    .join(" · ");
                  const isBest = idx === 0 && !search.trim();
                  return (
                    <Box
                      key={s.id}
                      onClick={() => setStaffId(s.id)}
                      sx={{
                        p: 1, borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 1.25,
                        border: `1px solid ${on ? theme.palette.primary.main : "rgba(15,23,42,0.12)"}`,
                        bgcolor: on ? alpha(theme.palette.primary.main, 0.06) : "#fff",
                        "&:hover": { borderColor: theme.palette.primary.main },
                      }}
                    >
                      <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }}>
                        {initials(s.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          {isBest && <StarRoundedIcon sx={{ fontSize: 15, color: theme.palette.warning.main }} />}
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{s.name}</Typography>
                          {score && (
                            <Chip
                              size="small"
                              label={`${isBest ? "Best Match " : ""}${score}`}
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.dark }}
                            />
                          )}
                        </Box>
                        {gs && (
                          <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {gs}
                          </Typography>
                        )}
                        <Box sx={{ mt: 0.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {(s.match_reasons || []).slice(0, 3).map((r, i) => (
                            <Typography key={i} sx={{ fontSize: 11, color: "success.main", display: "flex", alignItems: "center", gap: 0.25 }}>
                              <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> {r}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {!loadingSmart && ineligible.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<InfoOutlinedIcon fontSize="small" />}
                  onClick={() => setShowIneligible((v) => !v)}
                  sx={{ textTransform: "none", color: "text.secondary", fontSize: 12 }}
                >
                  {showIneligible ? "Hide" : "Show"} unavailable staff ({ineligible.length}) — why isn't someone listed?
                </Button>
                <Collapse in={showIneligible}>
                  <Box sx={{ mt: 0.5, p: 1, borderRadius: "8px", bgcolor: alpha("#0f172a", 0.03) }}>
                    {ineligible.map((s) => (
                      <Box key={s.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: alpha("#0f172a", 0.1), color: "text.secondary" }}>
                          {initials(s.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{s.name}</Typography>
                          {s.reasons.map((r, i) => (
                            <Typography key={i} sx={{ fontSize: 11, color: "text.secondary" }}>• {r}</Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {tried && !staffId && (
              <Typography sx={{ fontSize: 11.5, color: "error.main", mt: 0.5 }}>
                Select a replacement teacher.
              </Typography>
            )}
          </Box>

          <TextField
            select label="Reason *" size="small" fullWidth required
            value={reasonSel}
            onChange={(e) => { setReasonSel(e.target.value); if (e.target.value !== "Other") setCustomReason(""); }}
            error={tried && reasonSel === ""}
          >
            <MenuItem value="">Select a reason…</MenuItem>
            {SWITCH_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          {reasonSel === "Other" && (
            <TextField
              label="Specify reason *" size="small" fullWidth
              value={customReason}
              inputProps={{ maxLength: REASON_MAX }}
              onChange={(e) => setCustomReason(e.target.value)}
              error={tried && customReason.trim().length === 0}
              helperText={tried && customReason.trim().length === 0 ? "Please enter the reason." : `${customReason.length}/${REASON_MAX}`}
            />
          )}

          <TextField
            label="Notes (optional)" size="small" fullWidth multiline minRows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)}
          />

          {futureCount > 0 && dateInRange && (
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              This switch will reassign <strong>{futureCount}</strong> future session{futureCount === 1 ? "" : "s"} starting {dayjs(effectiveDate).format("MMM D, YYYY")}.
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>Cancel</Button>
        <Tooltip title={!canSubmit && submitDisabledReason ? submitDisabledReason : ""} disableHoverListener={canSubmit}>
          <span>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
              sx={{ textTransform: "none", boxShadow: "none", borderRadius: "8px" }}
            >
              {saving ? "Switching…" : "Switch Staff"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SwitchStaffDialog;
